import * as React from 'react'
import { Plus } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { EventSalesDto, SalesPacketDto } from '@lib/dto/sales-packet.dto'
import type { SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import type { SalesSchedulesDto, SalesSettingsDto, SalesTemplatesDto } from '@lib/dto/sales-setup.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { packetLinesError, packetRange, packetScheduleError } from '@lib/helpers/sales-packet'
import type { ScheduleEntry } from '@lib/helpers/sales-schedule'
import { createAmendmentInputSchema, createPacketInputSchema, type CreatePacketInput } from '@lib/input/sales-packet.input'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { timeOfDay, type TimeOfDay } from '@lib/validators/time-of-day.validator'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePicker } from '@/components/ui/date-picker'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { TimePicker } from '@/components/ui/time-picker'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { useAuth } from '@/auth/AuthContext'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSalesPriceList, useSalesSchedules, useSalesSettings, useSalesTemplates, useSendAmendment, useSendPacket } from '@/hooks/useSales'
import { todayIso } from '@/lib/bookedDays'
import { clockLabel, formatEventDate } from '@/lib/eventTime'
import { centsToInput, inputToCents } from '@/lib/price'
import { lineFromAgreed, lineFromItem, linesOf, PacketLines, plusDays, quoteLines, type LineDraft } from './PacketLines'
import { ScheduleEntryRows } from './ScheduleEntryRows'
import { blankEntry, entriesOf, entryDraft, type EntryDraft } from './scheduleDraft'

/** The date to start from: the event's own, else the first date the couple asked about that's still ahead. */
function startingDate(event: EventDetailDto, today: CalendarDay): CalendarDay | '' {
  if (event.date !== null && event.date >= today) return event.date
  return event.requestedDates.find((d) => d >= today) ?? ''
}

/**
 * What a change's payments start from: the agreement's that come after
 * signing (what was due on signing is paid), or else one payment of the rest.
 */
function stillToCome(agreement: SalesPacketDto): ScheduleEntry[] {
  const ahead = agreement.schedule.filter((e) => e.due.kind !== 'on_signing')
  if (ahead.some((e) => e.amount.kind === 'rest')) return ahead
  return [{ label: 'Balance', amount: { kind: 'rest' }, due: { kind: 'before_event', days: 30 } }]
}

/** A signed packet's times as the pickers take them, or blank for the venue's usual. */
function timeOf(wall: string | null): TimeOfDay | '' {
  return wall === null ? '' : timeOfDay(wall.slice(11, 16))
}

/**
 * What's been paid toward the agreement, or is on its way: the booking's and
 * its changes' payments, not invoices. A change's schedule covers the rest.
 */
export function paidTowardAgreement(sales: EventSalesDto): number {
  const agreements = new Set(sales.packets.filter((p) => p.kind === 'booking' || p.kind === 'amendment').map((p) => p.id))
  return sales.payments
    .filter((p) => p.kind === 'installment' && agreements.has(p.packetId))
    .reduce((sum, p) => sum + (p.status === 'processing' ? Math.max(p.creditedCents, p.amountCents) : p.creditedCents), 0)
}

/** A proposal's fields without who signs: a change is signed by whoever signed the agreement. */
function withoutSigners(p: CreatePacketInput): Omit<CreatePacketInput, 'signerUserIds'> {
  return {
    date: p.date,
    startsAt: p.startsAt,
    endsAt: p.endsAt,
    lines: p.lines,
    contractTemplateId: p.contractTemplateId,
    schedule: p.schedule,
    securityDeposit: p.securityDeposit,
    countersignerId: p.countersignerId,
    validUntil: p.validUntil,
  }
}

interface BuilderData {
  priceList: SalesPriceListDto
  templates: SalesTemplatesDto
  schedules: SalesSchedulesDto
  settings: SalesSettingsDto
}

interface PacketBuilderProps {
  event: EventDetailDto
  sales: EventSalesDto
  /** The signed agreement a change is to: the builder sends a change to it instead of a proposal. */
  amends?: SalesPacketDto
  open: boolean
  onOpenChange: (open: boolean) => void
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
      {children}
    </section>
  )
}

interface BuilderProps {
  event: EventDetailDto
  sales: EventSalesDto
  amends: SalesPacketDto | undefined
  data: BuilderData
  onDone: () => void
}

function Builder({ event, sales, amends, data, onDone }: BuilderProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const send = useSendPacket(event.id)
  const sendChange = useSendAmendment(event.id)
  const pending = send.isPending || sendChange.isPending
  const today = todayIso()
  const [date, setDate] = React.useState<CalendarDay | ''>(amends?.date ?? startingDate(event, today))
  const [startsAt, setStartsAt] = React.useState<TimeOfDay | ''>(timeOf(amends?.startsAt ?? null))
  const [endsAt, setEndsAt] = React.useState<TimeOfDay | ''>(timeOf(amends?.endsAt ?? null))
  const [lines, setLines] = React.useState<LineDraft[]>(() => (amends?.lines ?? []).filter((l) => l.chosen).map(lineFromAgreed))
  const [templateId, setTemplateId] = React.useState(data.templates.templates[0]?.id ?? '')
  const [scheduleId, setScheduleId] = React.useState(amends === undefined ? (data.schedules.schedules[0]?.id ?? '') : '')
  const [entries, setEntries] = React.useState<EntryDraft[]>(() => (amends === undefined ? (data.schedules.schedules[0]?.entries ?? []) : stillToCome(amends)).map(entryDraft))
  const agreedDeposit = amends?.securityDeposit ?? null
  const [depositOn, setDepositOn] = React.useState(agreedDeposit !== null)
  const [deposit, setDeposit] = React.useState(agreedDeposit === null ? '' : centsToInput(agreedDeposit.cents))
  const [depositDue, setDepositDue] = React.useState<'on_signing' | 'before_event'>(agreedDeposit?.due.kind === 'on_signing' ? 'on_signing' : 'before_event')
  const [depositDays, setDepositDays] = React.useState(agreedDeposit?.due.kind === 'before_event' ? String(agreedDeposit.due.days) : '14')
  const [signers, setSigners] = React.useState<string[]>(sales.clients.map((c) => c.userId))
  const me = sales.countersigners.find((c) => c.userId === user?.id)
  const [countersignerId, setCountersignerId] = React.useState(me?.userId ?? sales.countersigners[0]?.userId ?? '')
  const [validUntil, setValidUntil] = React.useState<CalendarDay | ''>(plusDays(today, data.settings.validDays))
  const [error, setError] = React.useState<string | null>(null)

  const offered = data.priceList.items.filter((item) => item.trackId === null || event.trackIds.includes(item.trackId))
  const range = packetRange(quoteLines(lines), data.priceList.rates)
  // A change's payments cover what's left once what's been paid is taken off.
  const paid = amends === undefined ? 0 : paidTowardAgreement(sales)
  const left = { low: { ...range.low, totalCents: range.low.totalCents - paid }, high: { ...range.high, totalCents: range.high.totalCents - paid } }
  const usual = `${clockLabel(event.venue.defaultStartsAt)} to ${clockLabel(event.venue.defaultEndsAt)}`

  /** Everything as the API takes it, or the first thing to fix. */
  function packetOf(): CreatePacketInput | string {
    if (date === '') return 'Choose the date it’s for.'
    if ((startsAt === '') !== (endsAt === '')) return 'Set both times, or neither for the venue’s usual times.'
    const sent = linesOf(lines)
    if (typeof sent === 'string') return sent
    const linesError = packetLinesError(sent.map((l, i) => ({ ...l, key: String(i) })))
    if (linesError !== null) return linesError
    const schedule = entriesOf(entries)
    if (typeof schedule === 'string') return schedule
    if (left.low.totalCents < 0) return `More than the lowest new total has been paid already (${dollarsFromCents(paid)}). Refund the difference before sending this change.`
    const scheduleError = packetScheduleError(schedule, left, date, today)
    if (scheduleError !== null) return scheduleError
    let securityDeposit: CreatePacketInput['securityDeposit'] = null
    if (depositOn) {
      const cents = inputToCents(deposit)
      if (cents === null || cents === 0) return 'Enter the security deposit in dollars.'
      const days = Number.parseInt(depositDays, 10)
      if (depositDue === 'before_event' && (!Number.isInteger(days) || days < 0 || days > 730)) return 'Enter how many days before the event the security deposit is due.'
      securityDeposit = { cents, due: depositDue === 'on_signing' ? { kind: 'on_signing' } : { kind: 'before_event', days } }
    }
    if (validUntil === '') return amends === undefined ? 'Choose the last day the couple can accept it.' : 'Choose the last day the couple can sign it.'
    const fields = {
      date,
      startsAt: startsAt === '' ? null : startsAt,
      endsAt: endsAt === '' ? null : endsAt,
      lines: sent,
      contractTemplateId: templateId,
      schedule,
      securityDeposit,
      countersignerId,
      validUntil,
    }
    const parsed = createPacketInputSchema.safeParse({ ...fields, signerUserIds: amends === undefined ? signers : amends.signers.map((s) => s.userId) })
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      if (issue?.path[0] === 'contractTemplateId') return 'Choose a contract.'
      if (issue?.path[0] === 'countersignerId') return 'Choose who countersigns.'
      return issue?.message ?? 'Something here isn’t complete.'
    }
    return parsed.data
  }

  /** Who signs: the agreement's signers for a change, else the clients to choose from. */
  function signersOf(): React.ReactNode {
    if (amends !== undefined) {
      return (
        <p className="m-0 text-sm text-ink">
          {amends.signers.map((s) => s.name).join(' and ')}, who signed the agreement. <span className="text-ink-muted">A change is signed by the same people.</span>
        </p>
      )
    }
    if (sales.clients.length === 0) return <p className="m-0 text-sm text-ink-muted">Add the couple as clients on this inquiry first; they sign it.</p>
    return (
      <ul className="m-0 list-none space-y-1 p-0">
        {sales.clients.map((c) => (
          <li key={c.userId}>
            <label className="flex items-center gap-2 text-sm text-ink">
              <Checkbox
                checked={signers.includes(c.userId)}
                onCheckedChange={(on) => setSigners((all) => (on ? [...all, c.userId] : all.filter((id) => id !== c.userId)))}
              />
              {c.name} <span className="text-ink-muted">{c.email}</span>
            </label>
          </li>
        ))}
      </ul>
    )
  }

  function handleSend(): void {
    const packet = packetOf()
    if (typeof packet === 'string') return setError(packet)
    setError(null)
    const what = amends === undefined ? 'Proposal' : 'Change'
    const handlers = {
      onSuccess: (sent: { warnings: string[] }) => {
        toast(sent.warnings.length === 0 ? `${what} sent.` : `${what} sent. ${sent.warnings.join(' ')}`)
        onDone()
      },
      onError: (e: Error) => setError(requestErrorMessage(e)),
    }
    if (amends === undefined) return send.mutate(packet, handlers)
    const change = createAmendmentInputSchema.safeParse({ ...withoutSigners(packet), amendsPacketId: amends.id })
    if (!change.success) return setError(change.error.issues[0]?.message ?? 'Something here isn’t complete.')
    sendChange.mutate(change.data, handlers)
  }

  return (
    <>
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-5">
        <Section title="When">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Date" htmlFor="packet-date">
              <DatePicker id="packet-date" value={date} onChange={setDate} min={today} />
            </Field>
            <Field label="Starts" htmlFor="packet-starts">
              <TimePicker value={startsAt} onChange={setStartsAt} ariaLabel="Starts" placeholder="Usual" />
            </Field>
            <Field label="Ends" htmlFor="packet-ends">
              <TimePicker value={endsAt} onChange={setEndsAt} ariaLabel="Ends" placeholder="Usual" />
            </Field>
          </div>
          <p className="m-0 text-xs text-ink-muted">
            Without times it’s for the venue’s usual {usual}.
            {event.requestedDates.length > 0 ? ` The couple asked about ${event.requestedDates.map((d) => formatEventDate(d)).join(', ')}.` : ''}
          </p>
        </Section>

        <Section title="What’s offered">
          <PacketLines
            lines={lines}
            onChange={setLines}
            items={offered}
            onAddItem={(item) => setLines((all) => [...all, lineFromItem(item, data.priceList, date, event.guestCount)])}
            withGroups
            empty="Add packages for the couple to pick from, add-ons, and anything included."
          />
          {lines.length === 0 ? null : (
            <p className="m-0 text-sm text-ink">
              {range.low.totalCents === range.high.totalCents
                ? `Comes to ${dollarsFromCents(range.low.totalCents)}`
                : `Comes to ${dollarsFromCents(range.low.totalCents)} to ${dollarsFromCents(range.high.totalCents)}, depending on what the couple picks`}
              , with tax and service charge.
              {paid > 0 ? ` ${dollarsFromCents(paid)} is paid already; the payments below cover the rest.` : ''}
            </p>
          )}
        </Section>

        <Section title="Contract and payments">
          <div className="flex flex-wrap gap-4">
            <Field label="Contract" htmlFor="packet-template">
              <Select id="packet-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                {data.templates.templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Start from schedule" htmlFor="packet-schedule">
              <Select
                id="packet-schedule"
                value={scheduleId}
                onChange={(e) => {
                  const chosen = data.schedules.schedules.find((s) => s.id === e.target.value)
                  setScheduleId(e.target.value)
                  if (chosen !== undefined) setEntries(chosen.entries.map(entryDraft))
                  if (chosen === undefined && amends !== undefined) setEntries(stillToCome(amends).map(entryDraft))
                }}
              >
                {amends === undefined ? null : <option value="">The agreement’s payments still to come</option>}
                {data.schedules.schedules.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <ScheduleEntryRows entries={entries} onChange={setEntries} allowDates noDeposit={amends !== undefined} />
          <Button variant="outline" size="sm" onClick={() => setEntries((all) => [...all, blankEntry(`Payment ${String(all.length + 1)}`, 'fixed')])}>
            <Plus className="h-3.5 w-3.5" />
            Add a payment
          </Button>
          <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
            <label className="flex items-center gap-2">
              <Checkbox checked={depositOn} onCheckedChange={setDepositOn} />
              Ask for a security deposit
            </label>
            {depositOn ? (
              <>
                <span className="text-ink-muted">$</span>
                <Input
                  size="sm"
                  inputMode="decimal"
                  aria-label="Security deposit"
                  className="w-24 text-right tabular-nums"
                  value={deposit}
                  onChange={(e) => setDeposit(e.target.value)}
                />
                <Select size="sm" aria-label="Security deposit due" value={depositDue} onChange={(e) => setDepositDue(e.target.value === 'before_event' ? 'before_event' : 'on_signing')}>
                  <option value="on_signing">Due on signing</option>
                  <option value="before_event">Due before the event</option>
                </Select>
                {depositDue === 'before_event' ? (
                  <>
                    <Input
                      size="sm"
                      inputMode="numeric"
                      aria-label="Days before the event the security deposit is due"
                      className="w-16 text-right tabular-nums"
                      value={depositDays}
                      onChange={(e) => setDepositDays(e.target.value)}
                    />
                    <span className="text-ink-muted">days</span>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </Section>

        <Section title="Who signs">
          {signersOf()}
          <div className="flex">
            <Field label="Countersigns for the venue" htmlFor="packet-countersigner">
              <Select id="packet-countersigner" value={countersignerId} onChange={(e) => setCountersignerId(e.target.value)}>
                {sales.countersigners.map((c) => (
                  <option key={c.userId} value={c.userId}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Open until">
          <div className="flex">
            <Field
              label={amends === undefined ? 'Last day the couple can accept it' : 'Last day the couple can sign it'}
              htmlFor="packet-valid-until"
              hint={amends === undefined ? 'It holds the date until then.' : 'Until it’s signed, the agreement stays as it is.'}
            >
              <DatePicker id="packet-valid-until" value={validUntil} onChange={setValidUntil} min={today} {...(date === '' ? {} : { max: date })} />
            </Field>
          </div>
        </Section>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-line px-6 py-4">
        {error === null ? null : <FormError className="mr-auto">{error}</FormError>}
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={handleSend} disabled={pending}>
          {pending ? 'Sending…' : amends === undefined ? 'Send proposal' : 'Send the change'}
        </Button>
      </div>
    </>
  )
}

/** Building a proposal for an inquiry, in a wide sheet (docs/sales.spec.md, "Sending a packet"). */
export function PacketBuilder({ event, sales, amends, open, onOpenChange }: PacketBuilderProps) {
  const priceList = useSalesPriceList(event.venueId)
  const templates = useSalesTemplates(event.venueId)
  const schedules = useSalesSchedules(event.venueId)
  const settings = useSalesSettings(event.venueId)
  const queries = [priceList, templates, schedules, settings]
  const failed = queries.find((q) => q.isError)

  function body() {
    if (failed !== undefined) return <ErrorState message={requestErrorMessage(failed.error)} onRetry={() => queries.forEach((q) => void q.refetch())} />
    if (priceList.data === undefined || templates.data === undefined || schedules.data === undefined || settings.data === undefined) return <DetailSkeleton />
    return (
      <Builder
        event={event}
        sales={sales}
        amends={amends}
        data={{ priceList: priceList.data, templates: templates.data, schedules: schedules.data, settings: settings.data }}
        onDone={() => onOpenChange(false)}
      />
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-[960px]">
        <SheetHeader>
          <SheetTitle>{amends === undefined ? 'Send a proposal' : 'Change the agreement'}</SheetTitle>
          <SheetDescription>
            {amends === undefined
              ? `To ${event.name}. It holds the date while it’s out.`
              : `To ${event.name}. Once it’s signed, its total and payments replace what’s still owed, and the event moves if the date or times change.`}
          </SheetDescription>
        </SheetHeader>
        {body()}
      </SheetContent>
    </Sheet>
  )
}
